import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, BadgeCheck, Map } from "lucide-react";
import Mandala from "./Mandala";
import Particles from "./Particles";
import { scrollToId } from "../lib/scroll";
import { IMG } from "../site";
import { EASE } from "./Reveal";

const D = 1.75;

function Line({ delay, gold, children }) {
    return (
        <span className="block overflow-hidden pb-1 -mb-1">
            <motion.span
                className={`block ${gold ? "text-gold-grad" : "text-white"}`}
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay, ease: EASE }}
            >
                {children}
            </motion.span>
        </span>
    );
}

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const yPhoto = useTransform(scrollYProgress, [0, 1], [0, 90]);
    const yMandala = useTransform(scrollYProgress, [0, 1], [0, 150]);

    return (
        <section
            id="home"
            ref={ref}
            data-testid="hero-section"
            className="relative flex min-h-screen items-center overflow-hidden pt-[72px]"
        >
            {/* ambient background */}
            <div className="pointer-events-none absolute inset-0">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_75%_35%,rgba(212,160,23,0.13),transparent_55%)]" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_15%_85%,rgba(232,137,22,0.07),transparent_50%)]" />
                <motion.div
                    style={{ y: yMandala }}
                    className="absolute -right-[16%] top-1/2 hidden -translate-y-1/2 md:block"
                >
                    <Mandala className="h-[900px] w-[900px] animate-spin-slower opacity-40" />
                </motion.div>
            </div>
            <Particles className="absolute inset-0 h-full w-full" />

            <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-14 px-4 pb-24 pt-10 sm:px-8 lg:grid-cols-[1.15fr_0.85fr] lg:gap-6 lg:px-16">
                {/* left — type */}
                <div>
                    <motion.div
                        className="mb-7 flex items-center gap-3"
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: D, ease: EASE }}
                    >
                        <span className="h-px w-12 bg-gradient-to-r from-transparent to-[#F5C542]" />
                        <span className="text-[11px] font-bold uppercase tracking-[0.35em] text-[#F5C542]">
                            Advance Yoga Coaching
                        </span>
                    </motion.div>

                    <h1 className="font-display text-[13.5vw] leading-[0.94] sm:text-7xl lg:text-[5.4rem] xl:text-[6rem]">
                        <Line delay={D + 0.1}>MASTER YOUR ASANA.</Line>
                        <Line delay={D + 0.24} gold>
                            MASTER YOUR PERFORMANCE.
                        </Line>
                    </h1>

                    <motion.p
                        className="mt-6 max-w-md text-[15px] leading-relaxed text-[#B8B8B8] sm:text-base"
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: D + 0.55, ease: EASE }}
                    >
                        Competition-Level Yogasana Training for Serious Yogis,
                        Athletes &amp; Future Champions.
                    </motion.p>

                    <motion.div
                        className="mt-8 flex items-center gap-4"
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: D + 0.7, ease: EASE }}
                    >
                        <span className="h-12 w-px bg-gradient-to-b from-[#F5C542] to-transparent" />
                        <div>
                            <p className="font-display text-3xl tracking-[0.12em] text-white">
                                TRAINER ARYAN
                            </p>
                            <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.28em] text-[#D4A017]">
                                Certified National Yoga Coach
                            </p>
                        </div>
                    </motion.div>

                    <motion.div
                        className="mt-10 flex flex-wrap items-center gap-4"
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: D + 0.85, ease: EASE }}
                    >
                        <button
                            data-testid="hero-cta-start-training"
                            onClick={() => scrollToId("contact")}
                            className="btn-gold"
                        >
                            Start Training <ArrowRight size={15} />
                        </button>
                        <button
                            data-testid="hero-cta-view-journey"
                            onClick={() => scrollToId("journey")}
                            className="btn-ghost"
                        >
                            <Map size={15} /> View My Journey
                        </button>
                    </motion.div>
                </div>

                {/* right — athlete */}
                <motion.div
                    style={{ y: yPhoto }}
                    className="relative mx-auto mt-6 w-full max-w-[300px] sm:mt-0 sm:max-w-[430px]"
                    initial={{ opacity: 0, scale: 0.94, y: 40 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 1.2, delay: D + 0.3, ease: EASE }}
                >
                    <div className="absolute left-1/2 top-1/2 -z-10 h-[560px] w-[560px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(245,197,66,0.22),rgba(212,160,23,0.07)_48%,transparent_72%)] blur-2xl" />
                    <Mandala className="absolute left-1/2 top-1/2 -z-10 h-[540px] w-[540px] -translate-x-1/2 -translate-y-1/2 animate-spin-slow opacity-60" />

                    <div className="gold-frame rounded-b-3xl rounded-t-[999px] p-[3px] shadow-[0_40px_120px_-30px_rgba(0,0,0,0.9)]">
                        <div className="relative overflow-hidden rounded-b-3xl rounded-t-[999px]">
                            <img
                                src={IMG.hero}
                                alt="Trainer Aryan performing an advanced yogasana arm balance"
                                className="aspect-[3/4] w-full object-cover object-center grayscale contrast-110"
                                loading="eager"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/30" />
                            <div className="absolute inset-0 bg-[#D4A017]/10 mix-blend-overlay" />
                        </div>
                    </div>

                    <div className="glass animate-float absolute -left-10 bottom-16 flex items-center gap-2.5 rounded-full px-5 py-3">
                        <BadgeCheck size={17} className="text-[#F5C542]" />
                        <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/90">
                            National Yoga Coach
                        </span>
                    </div>
                </motion.div>
            </div>

            {/* scroll hint */}
            <motion.div
                className="absolute bottom-7 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-3"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: D + 1.3, duration: 0.8 }}
            >
                <span className="text-[9px] font-semibold uppercase tracking-[0.4em] text-white/40">
                    Scroll
                </span>
                <span className="h-12 w-px animate-scroll-hint bg-gradient-to-b from-[#F5C542] to-transparent" />
            </motion.div>
        </section>
    );
}
